import { useMemo, useState } from "react";
import {
  MAX_PLAYERS,
  MIN_PLAYERS,
  ROUND_TIERS,
  SEGMENT_TIERS,
  subChunkCount,
} from "../../convex/lib/game";
import { useClipActions, useRoomActions } from "../components/backend";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { ClipSlot } from "../components/ClipSlot";
import type {
  Auth,
  Clip,
  Player,
  RoomState,
  RoundCount,
  SegmentLength,
  ToastTone,
} from "../components/contracts";
import { Footer } from "../components/Footer";
import { NavBar, NavWord } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { PlayerRow } from "../components/PlayerRow";
import { RoomCode } from "../components/RoomCode";
import { cx, errorText } from "../components/util";

export interface LobbyProps {
  room: RoomState;
  auth: Auth;
  onPickSegment: (clip: Clip) => void;
  onLeave: () => void;
  onToast: (text: string, tone?: ToastTone) => void;
}

export function Lobby({ room, auth, onPickSegment, onLeave, onToast }: LobbyProps) {
  const { setSettings, start } = useRoomActions();
  const { setNeedClip, donate } = useClipActions();
  const [starting, setStarting] = useState(false);
  const [donatingFor, setDonatingFor] = useState<string | null>(null);

  const me = room.players.find((p) => p.playerId === auth.playerId);
  const isHost = me?.isHost ?? false;

  const myClips = useMemo(
    () => room.clips.filter((c) => c.ownerId === auth.playerId),
    [room.clips, auth.playerId],
  );
  const donated = useMemo(
    () => room.clips.filter((c) => c.donatedBy === auth.playerId),
    [room.clips, auth.playerId],
  );

  /**
   * Donation moves ownership, so you must be holding a spare — otherwise you
   * would be giving away the only clip covering you. The server enforces this
   * too; the button just stops you finding out the hard way.
   */
  const spare = myClips.filter((c) => c.dealable).length >= 2;

  const uncovered = room.players.filter((p) => !p.covered);
  // Mirrors the server's floor in startGame — a solo test room runs with one.
  const minPlayers = room.soloTest ? 1 : MIN_PLAYERS;
  const countOk = room.players.length >= minPlayers && room.players.length <= MAX_PLAYERS;
  const canStart = isHost && countOk && uncovered.length === 0;

  const missing = minPlayers - room.players.length;
  const blockReason = !countOk
    ? missing > 0
      ? `Need ${missing} more player${missing === 1 ? "" : "s"}.`
      : `Too many players. Maximum is ${MAX_PLAYERS}.`
    : room.waitingOn.length > 0
      ? `Waiting on a clip for ${room.waitingOn.join(", ")}.`
      : null;

  async function handleStart() {
    setStarting(true);
    try {
      await start(auth);
    } catch (err) {
      onToast(errorText(err, "Couldn't start the game."), "error");
    } finally {
      setStarting(false);
    }
  }

  async function handleDonate(target: Player) {
    const gift = myClips.find((c) => c.dealable);
    if (!gift) return;
    setDonatingFor(target.playerId);
    try {
      await donate(auth, gift.clipId, target.playerId);
      onToast(`${target.name} is covered.`, "success");
    } catch (err) {
      onToast(errorText(err, "Couldn't donate that clip."), "error");
    } finally {
      setDonatingFor(null);
    }
  }

  return (
    <div className="bd-screen">
      <NavBar
        subnav={
          <>
            <span>Lobby</span>
            <span aria-hidden="true">&middot;</span>
            <span>
              {room.players.length}/{MAX_PLAYERS} players
            </span>
            <span aria-hidden="true">&middot;</span>
            <span>
              {room.roundCount} rounds &times; {room.segmentLength}s
            </span>
          </>
        }
        actions={<NavWord onClick={onLeave}>Leave</NavWord>}
      />

      <main className="shell bd-stack-lg">
        <div className="plate chamfer bd-pad-lg bd-slide-in">
          <RoomCode code={room.code} onCopied={(m) => onToast(m, "success")} />
          <p className="t-body center muted">
            Anyone on the same wifi or not &mdash; the code is all they need.
          </p>
        </div>

        <div className="split">
          <div>
            <Panel title="Players" aside={<Badge tone="carbon">{room.players.length}</Badge>}>
              <div className="bd-stagger">
                {room.players.map((player, i) => (
                  <PlayerRow
                    key={player.playerId}
                    player={player}
                    index={i}
                    isSelf={player.playerId === auth.playerId}
                    readyLabel="Clip in"
                  >
                    {!player.covered && player.playerId !== auth.playerId ? (
                      <Button
                        variant="primary"
                        disabled={donatingFor === player.playerId || !spare}
                        onClick={() => void handleDonate(player)}
                      >
                        Cover them
                      </Button>
                    ) : null}
                  </PlayerRow>
                ))}
              </div>

              {blockReason ? (
                <p className="t-body muted center" role="status">
                  {blockReason}
                </p>
              ) : null}
            </Panel>

            <Panel title="Your clip">
              <ClipSlot
                auth={auth}
                clips={myClips}
                segmentLength={room.segmentLength}
                onPickSegment={onPickSegment}
                onToast={onToast}
              />

              <hr className="dotted-divider" />

              <label className="row">
                <input
                  type="checkbox"
                  checked={me?.needClip ?? false}
                  onChange={(e) => void setNeedClip(auth, e.target.checked)}
                />
                <span className="t-link grow">I don&rsquo;t have a clip</span>
                {me?.covered && me.needClip ? <Badge tone="ready">Covered</Badge> : null}
              </label>
              <p className="t-micro muted">
                Someone with a spare can hand one over. You still dub &mdash; you just
                didn&rsquo;t supply.
              </p>

              {donated.length > 0 ? (
                <>
                  <hr className="dotted-divider" />
                  <p className="t-label">Clips you donated</p>
                  {donated.map((clip) => (
                    <div key={clip.clipId} className="row">
                      <span className="t-link grow truncate">Donated clip</span>
                      <Badge tone="amber">
                        for{" "}
                        {room.players.find((p) => p.playerId === clip.ownerId)?.name ??
                          "someone"}
                      </Badge>
                    </div>
                  ))}
                </>
              ) : null}
            </Panel>
          </div>

          <aside>
            <Panel title="Settings" aside={isHost ? <Badge tone="host">Host</Badge> : null}>
              <TierPicker
                legend="Segment length"
                hint={(v) => `${subChunkCount(v)} sub-chunks to record`}
                options={SEGMENT_TIERS}
                value={room.segmentLength}
                unit="s"
                disabled={!isHost}
                onChange={(v) =>
                  void setSettings(auth, { segmentLength: v as SegmentLength }).catch(
                    (err: unknown) =>
                      onToast(
                        errorText(err, "Couldn't change that."),
                        "error",
                      ),
                  )
                }
              />
              <hr className="dotted-divider" />
              <TierPicker
                legend="Rounds"
                options={ROUND_TIERS}
                value={room.roundCount}
                disabled={!isHost}
                onChange={(v) =>
                  void setSettings(auth, { roundCount: v as RoundCount }).catch(
                    (err: unknown) =>
                      onToast(
                        errorText(err, "Couldn't change that."),
                        "error",
                      ),
                  )
                }
              />
              {!isHost ? (
                <p className="t-micro muted">Only the host can change these.</p>
              ) : null}
            </Panel>

            <Panel title="Start" animate={false}>
              <Button
                variant="submit"
                block
                beacon={canStart}
                disabled={!canStart || starting}
                onClick={() => void handleStart()}
              >
                {starting ? "Dealing…" : "Start game"}
              </Button>
              {!isHost ? (
                <p className="t-micro muted center">Waiting for the host.</p>
              ) : null}
            </Panel>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function TierPicker({
  legend,
  options,
  value,
  unit = "",
  hint,
  disabled,
  onChange,
}: {
  legend: string;
  options: readonly number[];
  value: number;
  unit?: string;
  hint?: (value: number) => string;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <fieldset className="bd-fieldset">
      <legend className="t-label">{legend}</legend>
      <div className="bd-radio-group">
        {options.map((option) => (
          <label key={option} className={cx("bd-radio", "chip")} data-checked={option === value}>
            <input
              type="radio"
              name={legend}
              value={option}
              checked={option === value}
              disabled={disabled}
              onChange={() => onChange(option)}
            />
            <span className="bd-radio-dot" aria-hidden="true" />
            <span className="t-label">
              {option}
              {unit}
            </span>
          </label>
        ))}
      </div>
      {hint ? <p className="t-micro muted">{hint(value)}</p> : null}
    </fieldset>
  );
}
